import type { MedplumClient, ProfileResource } from "@medplum/core";
import { formatHumanName } from "@medplum/core";
import { AgentChat as AgentChatProvider, createAgentChat } from "@repo/agent-chat";
import { visitIdSchema } from "@repo/api/schemas";
import type { Permissions } from "@repo/auth";
import { usePermissionsContext } from "@repo/auth";
import { useMediaReadiness } from "@repo/conferencing/components";
import { cancelVisit, completeVisit, useOrderSets, usePatients, useVisit, useVisits } from "@repo/data-store";
import { Patient as PatientModel } from "@repo/domain";
import { useTheme } from "@repo/ui/components/theme-provider";
import { SidebarInset, SidebarProvider } from "@repo/ui/components/ui/sidebar";
import { Toaster } from "@repo/ui/components/ui/sonner";
import {
  createRootRouteWithContext,
  Outlet,
  redirect,
  useNavigate,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import {
  BookText,
  Calendar,
  ClipboardCheck,
  Inbox,
  LayoutDashboard,
  MessageSquare,
  Settings,
  Shield,
  Users,
} from "lucide-react";
import { useCallback, useMemo } from "react";
import { toast } from "sonner";
import { FloatingPrompt } from "@/app/command-palette/floating-prompt";
import { paletteStore } from "@/app/command-palette/palette-store";
import { useFloatingPrompt } from "@/app/command-palette/use-floating-prompt";
import { useNotificationToast } from "@/app/providers/use-notification-toast";
import { AppHeader } from "@/app/shell/app-header";
import { AppSidebar, type NavItem } from "@/app/shell/app-sidebar";
import { GlobalOverlays } from "@/app/shell/global-overlays";
import { RouteError } from "@/app/shell/route-error";
import { useBreadcrumbData } from "@/app/shell/use-breadcrumb-data";
import { AppointmentDetailPanel } from "@/app/surfaces/appointment-detail-panel";
import { ChatRouteContextSync } from "@/features/chat/components/agent-chat/route-context-sync";
import { AgentChatPanel } from "@/features/chat/components/agent-chat-panel";
import { CALL_ANCHOR_ID } from "@/features/conferencing/conferencing-host";
import {
  SessionPanel,
  useAppointmentDetailActions,
  useAppointmentPanelOpen,
  useDetailScheduleId,
  useSelectedAppointmentId,
} from "@/features/scheduling";
import { practitionerFhirIdFromProfile } from "@/shared/hooks/use-current-practitioner";
import { AI_ENGINE_URL } from "@/shared/lib/ai-engine-url";
import { patientDisplayName } from "@/shared/lib/patient-utils";
import { visitCompletionReason } from "@/shared/lib/visit-completion";

interface RouterContext {
  medplum: MedplumClient;
  permissions: Permissions;
  profile: ProfileResource | undefined;
}

// Hoisted so the client `useMemo` below (keyed on this reference) doesn't
// recompute on every render of the root layout — an inline object literal
// would be a new reference each time.
const PROVIDER_CHAT_DEFAULTS = { role: "clinical" } as const;

const navItems: NavItem[] = [
  {
    title: "Dashboard",
    url: "/dashboard",
    icon: LayoutDashboard,
  },
  {
    title: "Inbox",
    url: "/inbox",
    icon: Inbox,
  },
  {
    title: "Patients",
    url: "/patients",
    icon: Users,
  },
  {
    title: "Schedule",
    url: "/schedule",
    icon: Calendar,
  },
  {
    title: "Tasks",
    url: "/tasks",
    icon: ClipboardCheck,
  },
  {
    title: "Catalog",
    url: "/catalog",
    icon: BookText,
  },
  {
    title: "Chat",
    url: "/chat",
    icon: MessageSquare,
  },
  {
    title: "Settings",
    url: "/settings",
    icon: Settings,
  },
];

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: ({ context, location }) => {
    // Skip auth check for login page
    if (location.pathname === "/login") {
      return;
    }
    const profile = context.medplum.getProfile();
    if (!profile) {
      throw redirect({
        to: "/login",
        search: {
          redirect: location.href,
        },
      });
    }
  },
  component: RootComponent,
  errorComponent: RouteError,
});

function RootComponent() {
  useMediaReadiness();
  const location = useRouterState({ select: (s) => s.location });

  if (location.pathname === "/login") {
    return <Outlet />;
  }

  return <LayoutWrapper />;
}

function AppointmentPanel() {
  const visitId = useSelectedAppointmentId();
  const open = useAppointmentPanelOpen();
  const { closeDetail } = useAppointmentDetailActions();
  const storeScheduleId = useDetailScheduleId();

  const { visit } = useVisit(visitId ?? undefined);

  return (
    <AppointmentDetailPanel
      visitId={visitId}
      open={open}
      onClose={closeDetail}
      visit={visit}
      scheduleId={storeScheduleId}
    />
  );
}

function LayoutWrapper() {
  const { medplum, profile, permissions } = Route.useRouteContext();
  const router = useRouter();
  const navigate = useNavigate();
  // Two-state toggle: render off the RESOLVED theme. `theme` may be "system",
  // which is a preference, not something to display.
  const { resolvedTheme, setTheme } = useTheme();
  const { clear } = usePermissionsContext();
  useFloatingPrompt();
  useNotificationToast();

  const chatClient = useMemo(
    () => createAgentChat({ auth: medplum, baseUrl: AI_ENGINE_URL, defaults: PROVIDER_CHAT_DEFAULTS }),
    [medplum],
  );

  // Lifted hooks for child components
  const { visits: inProgressVisits } = useVisits({ status: ["inProgress"] });
  const inProgressOrderSets = useOrderSets({
    ids: inProgressVisits.flatMap((visit) => (visit.orderSetId ? [visit.orderSetId] : [])),
  });
  const completionReasons = useMemo(() => {
    const setsById = new Map(inProgressOrderSets.map((set) => [set.orderSetId, set]));
    return new Map(
      inProgressVisits.map((visit) => [
        visit.visitId,
        visitCompletionReason(visit, visit.orderSetId ? setsById.get(visit.orderSetId) : undefined),
      ]),
    );
  }, [inProgressVisits, inProgressOrderSets]);
  const breadcrumbData = useBreadcrumbData();

  // Quick actions: patients assigned to me (via case care-manager) that aren't discharged.
  const careManagerId = practitionerFhirIdFromProfile(profile);
  const { patients: activePatientDtos } = usePatients(careManagerId ? { careManagerId } : undefined);
  const activePatients = useMemo(
    () =>
      activePatientDtos.map((p) => ({
        patientId: p.patientId,
        label: patientDisplayName(p) ?? "Unnamed patient",
      })),
    [activePatientDtos],
  );

  // Session panel mutations (4a)
  const handleCancelAppt = useCallback(async (id: string) => {
    try {
      await cancelVisit({ visitId: visitIdSchema.parse(id) }).isPersisted.promise;
      toast.success("Appointment cancelled");
    } catch {
      // Domain errors surface globally via the tRPC error link.
    }
  }, []);
  const handleCompleteAppt = useCallback(async (id: string) => {
    try {
      await completeVisit({ visitId: visitIdSchema.parse(id) }).isPersisted.promise;
      toast.success("Appointment completed");
    } catch {
      // Domain errors surface globally via the tRPC error link.
    }
  }, []);

  const name = profile?.name
    ? formatHumanName(profile.resourceType === "Patient" ? PatientModel.displayName(profile.name) : profile.name[0])
    : "User";
  const email = profile?.telecom?.find((t) => t.system === "email")?.value || "user@example.com";
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  const handleLogout = async () => {
    await medplum.signOut();
    clear();
    await router.invalidate();
    await navigate({ to: "/" });
  };

  return (
    <AgentChatProvider.Provider client={chatClient}>
      <ChatRouteContextSync />
      <SidebarProvider defaultOpen={false} className="h-svh">
        <AppSidebar
          navItems={navItems}
          adminItem={permissions.isAdmin ? { title: "Admin", url: "/admin", icon: Shield } : undefined}
          activePatients={activePatients}
          user={{ name, email, initials }}
          theme={resolvedTheme}
          onThemeChange={setTheme}
          onLogout={handleLogout}
          onOpenCommandPalette={() => paletteStore.send({ type: "palette.open" })}
        />
        <SidebarInset className="overflow-hidden">
          <AppHeader breadcrumbData={breadcrumbData} inProgressVisits={inProgressVisits} />
          <div className="flex flex-1 min-h-0">
            {/* `id` is the call surface's anchor: expanded, it is measured from
                this region so it covers the chart and not the sidebar, header or
                the way out. Absent, the geometry falls back to the whole
                viewport. See CALL_ANCHOR_ID. */}
            <div id={CALL_ANCHOR_ID} className="flex-1 min-w-0 overflow-auto">
              <Outlet />
            </div>
            <AppointmentPanel />
            <SessionPanel
              visits={inProgressVisits}
              completionReasons={completionReasons}
              onCancel={handleCancelAppt}
              onComplete={handleCompleteAppt}
            />
            {/* patients also drives CommandPalette below */}
          </div>
        </SidebarInset>
        {/* Outside the inset so the rail spans full viewport height, mirroring AppSidebar. */}
        <AgentChatPanel onOpenHistory={() => paletteStore.send({ type: "palette.open", view: "chat-history" })} />
        <GlobalOverlays />
        <FloatingPrompt onOpenHistory={() => paletteStore.send({ type: "palette.open", view: "chat-history" })} />
        <Toaster />
      </SidebarProvider>
    </AgentChatProvider.Provider>
  );
}
